/**
 * Audit Log Service
 * Provides read-only access to system audit trails
 * All audit logs are automatically created via AOP - no manual logging required
 */

import { BaseService } from './BaseService';
import { parseUTCDate, formatLocalDateTime, formatLocalTime, formatTimeAgo } from '../utils/dateUtils';

class AuditLogService extends BaseService {
    constructor() {
        super('/api/audit-logs');
    }

    /**
     * Get recent audit logs with pagination
     * @param {string} accessToken - JWT access token
     * @param {number} page - Page number (default: 0)
     * @param {number} size - Page size (default: 50, max: 200)
     * @returns {Promise<Object>} Paginated response with audit logs
     */
    async getRecent(accessToken, page = 0, size = 50) {
        try {
            const response = await this.makeRequest(
                accessToken,
                `/recent?page=${page}&size=${size}`
            );
            return response;
        } catch (error) {
            console.error('Failed to fetch recent audit logs:', error);
            throw error;
        }
    }

    /**
     * Get audit logs for a specific user
     * @param {string} accessToken - JWT access token
     * @param {string} userOid - Azure AD user object ID (UUID)
     * @param {number} page - Page number (default: 0)
     * @param {number} size - Page size (default: 50)
     * @returns {Promise<Object>} Paginated response with user's audit logs
     */
    async getByUser(accessToken, userOid, page = 0, size = 50) {
        try {
            const response = await this.makeRequest(
                accessToken,
                `/user/${userOid}?page=${page}&size=${size}`
            );
            return response;
        } catch (error) {
            console.error(`Failed to fetch audit logs for user ${userOid}:`, error);
            throw error;
        }
    }

    /**
     * Get audit logs by action type
     * @param {string} accessToken - JWT access token
     * @param {string} action - Action type (CREATE, UPDATE, DELETE)
     * @param {number} page - Page number (default: 0)
     * @param {number} size - Page size (default: 50)
     * @returns {Promise<Object>} Paginated response with action-specific audit logs
     */
    async getByAction(accessToken, action, page = 0, size = 50) {
        try {
            const response = await this.makeRequest(
                accessToken,
                `/action/${action}?page=${page}&size=${size}`
            );
            return response;
        } catch (error) {
            console.error(`Failed to fetch audit logs for action ${action}:`, error);
            throw error;
        }
    }

    /**
     * Get audit logs for a specific resource
     * @param {string} accessToken - JWT access token
     * @param {string} resourceType - Type of resource (Document, Client, ClientType, NoteType)
     * @param {string} resourceId - ID of the resource
     * @param {number} page - Page number (default: 0)
     * @param {number} size - Page size (default: 50)
     * @returns {Promise<Object>} Paginated response with resource-specific audit logs
     */
    async getByResource(accessToken, resourceType, resourceId, page = 0, size = 50) {
        try {
            const response = await this.makeRequest(
                accessToken,
                `/resource/${resourceType}/${resourceId}?page=${page}&size=${size}`
            );
            return response;
        } catch (error) {
            console.error(`Failed to fetch audit logs for ${resourceType}/${resourceId}:`, error);
            throw error;
        }
    }

    /**
     * Get audit logs by correlation ID
     * @param {string} accessToken - JWT access token
     * @param {string} corrId - Correlation ID (UUID)
     * @returns {Promise<Array>} List of related audit logs (no pagination)
     */
    async getByCorrelationId(accessToken, corrId) {
        try {
            const response = await this.makeRequest(
                accessToken,
                `/correlation/${corrId}`
            );
            return response;
        } catch (error) {
            console.error(`Failed to fetch audit logs for correlation ID ${corrId}:`, error);
            throw error;
        }
    }

    /**
     * Get audit logs within a time range
     * @param {string} accessToken - JWT access token
     * @param {string} start - Start datetime (ISO 8601 format)
     * @param {string} end - End datetime (ISO 8601 format)
     * @param {number} page - Page number (default: 0)
     * @param {number} size - Page size (default: 50)
     * @returns {Promise<Object>} Paginated response with audit logs in time range
     */
    async getByTimeRange(accessToken, start, end, page = 0, size = 50) {
        try {
            const response = await this.makeRequest(
                accessToken,
                `/time-range?start=${encodeURIComponent(start)}&end=${encodeURIComponent(end)}&page=${page}&size=${size}`
            );
            return response;
        } catch (error) {
            console.error('Failed to fetch audit logs for time range:', error);
            throw error;
        }
    }

    /**
     * Get today's audit logs (midnight to now in UTC)
     * @param {string} accessToken - JWT access token
     * @param {number} page - Page number (default: 0)
     * @param {number} size - Page size (default: 50)
     * @returns {Promise<Object>} Paginated response with today's audit logs
     */
    async getToday(accessToken, page = 0, size = 50) {
        try {
            const response = await this.makeRequest(
                accessToken,
                `/today?page=${page}&size=${size}`
            );
            return response;
        } catch (error) {
            console.error('Failed to fetch today\'s audit logs:', error);
            throw error;
        }
    }

    /**
     * Get a specific audit log by ID
     * @param {string} accessToken - JWT access token
     * @param {number} id - Audit log ID
     * @returns {Promise<Object>} Single audit log object or 404 if not found
     */
    async getById(accessToken, id) {
        try {
            const response = await this.makeRequest(accessToken, `/${id}`);
            return response;
        } catch (error) {
            console.error(`Failed to fetch audit log ${id}:`, error);
            throw error;
        }
    }

    /**
     * Helper method: Get user's recent activity (last 20 items)
     * @param {string} accessToken - JWT access token
     * @param {string} userOid - Azure AD user object ID
     * @returns {Promise<Array>} Array of recent audit logs
     */
    async getUserRecentActivity(accessToken, userOid) {
        try {
            const response = await this.getByUser(accessToken, userOid, 0, 20);
            return response.content || [];
        } catch (error) {
            console.error('Failed to fetch user recent activity:', error);
            throw error;
        }
    }

    /**
     * Helper method: Get document history (complete audit trail)
     * @param {string} accessToken - JWT access token
     * @param {string} documentId - Document ID
     * @returns {Promise<Array>} Array of document audit logs
     */
    async getDocumentHistory(accessToken, documentId) {
        try {
            const response = await this.getByResource(accessToken, 'Document', documentId, 0, 100);
            return response.content || [];
        } catch (error) {
            console.error('Failed to fetch document history:', error);
            throw error;
        }
    }

    /**
     * Helper method: Get client history (complete audit trail)
     * @param {string} accessToken - JWT access token
     * @param {string} clientId - Client ID
     * @returns {Promise<Array>} Array of client audit logs
     */
    async getClientHistory(accessToken, clientId) {
        try {
            const response = await this.getByResource(accessToken, 'Client', clientId, 0, 100);
            return response.content || [];
        } catch (error) {
            console.error('Failed to fetch client history:', error);
            throw error;
        }
    }

    /**
     * Helper method: Format timestamp to relative time (e.g., "2 hours ago")
     * @param {string} timestamp - UTC timestamp from backend
     * @returns {string} Relative time string
     */
    formatTimeAgo(timestamp) {
        return formatTimeAgo(timestamp);
    }

    /**
     * Helper method: Format timestamp to local date/time string
     * @param {string} timestamp - UTC timestamp from backend
     * @returns {string} Formatted local date/time
     */
    formatLocalDateTime(timestamp) {
        return formatLocalDateTime(timestamp);
    }

    /**
     * Helper method: Format timestamp to local time only
     * @param {string} timestamp - UTC timestamp from backend
     * @returns {string} Formatted local time
     */
    formatLocalTime(timestamp) {
        return formatLocalTime(timestamp);
    }
}

// Export a singleton instance
const auditLogService = new AuditLogService();
export default auditLogService;
